/* BLANKED 3D viewer (theme asset). Binds every [data-b3d] stage on the page:
     <div class="b3d-stage" data-b3d data-src="…/product.glb" data-hotspots='[…]'> … </div>
   The product floats free: turn it all the way round (under it too), zoom in and out, and tap the dots to go to
   a feature. three.js loads only once a stage is near the viewport; frames are drawn on demand, never in a loop.
   Looks match the product photography: a white light tent that turns with the camera (so every angle reads like
   the product shot), baked contact shading from the GLB, the walnut taken from the product photo (data-walnut),
   and the surfaces built here by material name: alu / alu_engraved / walnut / felt, plus the silicone underside. */

const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/';
const BLUE = '#001bff';
const VIEWS = { '3/4': [35, 22], FRONT: [0, 3], SIDE: [90, 3], TOP: [0, 89], BOTTOM: [0, -89] };   // azimuth, elevation (degrees)
const EL_MIN = -89, EL_MAX = 89, ZOOM_MIN = 0.32, ZOOM_MAX = 2.8, HOME = 2.1;   // HOME: the resting zoom (product a little smaller in the frame)

let libs = null;
async function loadLibs() {
  if (libs) return libs;
  const [THREE, { GLTFLoader }, { DRACOLoader }] = await Promise.all([
    // jsDelivr's +esm builds resolve the add-ons' bare 'three' import to this same copy (no import map needed in a theme)
    import(THREE_URL + '+esm'),
    import(THREE_URL + 'examples/jsm/loaders/GLTFLoader.js/+esm'),
    import(THREE_URL + 'examples/jsm/loaders/DRACOLoader.js/+esm'),
  ]);
  libs = { THREE, GLTFLoader, DRACOLoader };
  return libs;
}

/* ---------- surfaces ---------- */
// object-space value noise + a procedural bump (three's perturbNormalArb, here without a texture)
const SURF = /* glsl */`
  varying vec3 vObj; varying vec3 vWorldN; varying vec3 vWorldP;
  uniform float b3dFloor; uniform float b3dWalTop;
  float b3dHash(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
  float b3dNoise(vec3 x) {
    vec3 i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(b3dHash(i), b3dHash(i + vec3(1,0,0)), f.x), mix(b3dHash(i + vec3(0,1,0)), b3dHash(i + vec3(1,1,0)), f.x), f.y),
               mix(mix(b3dHash(i + vec3(0,0,1)), b3dHash(i + vec3(1,0,1)), f.x), mix(b3dHash(i + vec3(0,1,1)), b3dHash(i + vec3(1,1,1)), f.x), f.y), f.z);
  }
  vec3 b3dBump(vec3 surf_pos, vec3 surf_norm, float h, float scale) {
    vec3 vSigmaX = normalize(dFdx(surf_pos)), vSigmaY = normalize(dFdy(surf_pos)), vN = surf_norm;
    vec3 R1 = cross(vSigmaY, vN), R2 = cross(vN, vSigmaX);
    float fDet = dot(vSigmaX, R1) * (gl_FrontFacing ? 1.0 : -1.0);
    vec2 dHdxy = vec2(dFdx(h), dFdy(h)) * scale;
    vec3 vGrad = sign(fDet) * (dHdxy.x * R1 + dHdxy.y * R2);
    return normalize(abs(fDet) * surf_norm - vGrad);
  }
  // the 3M silicone pad: faces that point straight down at the very bottom of the product
`;
function prep(s, floor, walTop) {
  s.uniforms.b3dFloor = floor; s.uniforms.b3dWalTop = walTop || { value: -1 };
  s.vertexShader = s.vertexShader
    .replace('#include <common>', '#include <common>\nvarying vec3 vObj; varying vec3 vWorldN; varying vec3 vWorldP;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvObj = position;\nvWorldP = (modelMatrix * vec4(position, 1.0)).xyz;\nvWorldN = normalize(mat3(modelMatrix) * normal);');
  s.fragmentShader = s.fragmentShader.replace('#include <common>', '#include <common>\n' + SURF);
}

function makeMaterials(THREE, floor, walTop, walRect, finish) {
  // all product materials are two-sided: CAD meshes carry hairline T-junction cracks, and a crack should show the
  // part's own inside, not the page behind it
  // bead-blasted, clear-anodised aluminium: bright silver, satin; the blast is a fine pitted bump that breaks the
  // reflections up, with a faint sparkle (roughness varies cell to cell). It fades out where a pixel covers many pits.
  const alu = (ao, mask, etch, coarse) => {   // coarse: big flat products (trays): softer, partly matte finish
    // finish "black": black anodise (the black valet tray): cool blue-black, matte; laser marks go light
    const BLK = finish === 'black';
    const m = new THREE.MeshPhysicalMaterial({ side: THREE.DoubleSide, color: BLK ? (etch ? +(self.B3D_BLK_ETCH || 0x8a8d92) : +(self.B3D_BLK_COL || 0x181a20)) : etch ? 0x9c9da0 : 0xc8c9cc, metalness: 1, roughness: etch ? 0.5 : 0.46, aoMap: ao || null, aoMapIntensity: 1 });
    m.onBeforeCompile = (s) => {
      prep(s, floor, walTop);
      if (mask) s.uniforms.b3dMask = { value: mask };
      s.fragmentShader = '#define B3D_PIT ' + (coarse ? +(self.B3D_PIT || 0.8) : 0.4).toFixed(3) + '\n' + s.fragmentShader;
      s.fragmentShader = s.fragmentShader
        .replace('#include <common>', '#include <common>' + (mask ? '\nuniform sampler2D b3dMask;' : ''))
        .replace('#include <color_fragment>', `#include <color_fragment>
          // bead blast: an even, fine grain (two fine layers, no broad blotches); it fades as pixels get bigger than the grains
          vec3 bp = vObj * 14000.0;                                                  // ~0.07 mm grains
          float aa = clamp(1.5 - length(fwidth(bp)) * 0.45, 0.3, 1.0);
          float pit = b3dNoise(bp) * 0.5 + b3dNoise(bp * 1.9 + 17.0) * 0.5;
          diffuseColor.rgb *= 0.98 + (pit - 0.5) * B3D_PIT * aa;
          ${BLK ? `// black anodise: the blast shows as a ~0.4 mm salt-and-pepper speckle (about 10% either way in his photos),
          // faded out once a pixel covers more than a couple of grains so it never crawls
          vec3 sc = vObj * 2600.0; float sk = clamp(1.6 - length(fwidth(sc)) * 0.55, 0.0, 1.0);
          float spk = b3dHash(floor(sc)) * 0.7 + b3dHash(floor(sc * 1.9) + 7.0) * 0.3;
          diffuseColor.rgb *= 1.0 + (spk - 0.5) * ${(+(self.B3D_BLK_GRAIN || 0.7)).toFixed(3)} * sk;` : ''}
          ${finish === 'brushed' ? `// hand brushing under the anodize (the desktop folder): fine vertical streaks (along y), uneven from
          // pass to pass: two streak layers at different pitches, each stroke a little stronger or weaker along its run
          vec3 bq = vec3(vObj.x * 6500.0, vObj.y * 90.0, vObj.z * 6500.0);
          float ab = clamp(1.6 - length(fwidth(bq.xz)) * 0.5, 0.0, 1.0);
          float pass = b3dNoise(vec3(vObj.x * 260.0, vObj.y * 35.0, vObj.z * 260.0));
          float brush = b3dNoise(bq) * 0.6 + b3dNoise(bq * vec3(2.9, 2.3, 2.9) + 31.0) * 0.4;
          brush = (brush - 0.5) * (0.6 + 0.8 * pass);
          diffuseColor.rgb *= 1.0 + brush * 0.07 * ab;
          pit = mix(pit, 0.5 + brush, 0.45 * ab);` : ''}
          ${mask ? 'float eng = texture2D(b3dMask, vMapUv).r; diffuseColor.rgb *= 1.0 - eng * 0.32;   // etched marks read as fine darker lines' : ''}
          // a faint warm bounce from the walnut on the metal just above it
          float bounce = b3dWalTop > 0.0 ? 1.0 - smoothstep(0.0, 0.012, vWorldP.y - b3dWalTop) : 0.0;
          diffuseColor.rgb *= mix(vec3(1.0), vec3(1.04, 0.985, 0.95), bounce * 0.6);`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
          roughnessFactor = clamp(roughnessFactor + (pit - 0.5) * 0.1 * aa ${mask ? '- eng * 0.1' : ''}, 0.12, 1.0);`)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
          normal = b3dBump(-vViewPosition, normal, pit, 0.000035 * aa * ${self.B3D_NOBUMP ? '0.0' : '1.0'});`);
      if (mask) s.fragmentShader = s.fragmentShader.replace('#include <map_fragment>', '');
   // the texture is a mask, not a colour
      // trays: their wide up-facing floors and rim would mirror the overhead softbox flat white; in the photos the
      // floor reads mid grey, darker than the lit walls, so up-facing faces take less of the key
      if (coarse) s.fragmentShader = s.fragmentShader.replace('#include <dithering_fragment>', '#include <dithering_fragment>\n          gl_FragColor.rgb *= mix(1.0, 0.85, smoothstep(0.7, 0.98, vWorldN.y));');
    };
    m.customProgramCacheKey = () => (mask ? 'b3d-alu-eng' : etch ? 'b3d-alu-etch' : 'b3d-alu') + (coarse ? '-c' : '') + (finish === 'brushed' ? '-b' : '') + (BLK ? '-k' : '');
    if (mask) { m.map = mask; mask.colorSpace = THREE.NoColorSpace; }
    if (coarse) { m.metalness = +(self.B3D_MET || 0.75); m.roughness = +(self.B3D_ROUGH || 0.4); }   // bead-blasted anodize on a big flat: mostly metal, so walls and fillets pick up the room's gradients
    if (coarse && BLK) { m.metalness = +(self.B3D_BLK_MET || 0.05); m.roughness = +(self.B3D_BLK_ROUGH || 0.9); }   // black anodise reads matte
    return m;
  };
  // solid American black walnut, oiled matte: Brandon's walnut photo, tiled and toned to the product photos
  // (data-walnut). The grain runs along each face and is blended through the
  // rounded corners, so it wraps without seams; its darker lines are read as fine grooves that catch the light.
  const walnut = (ao, wal, k = 1.0, su = 0.09, sv = su / 1.5, rect = null, sat = 0.72, off = [0, 0], along = false, tint = null) => {   // tint: an r,g,b multiplier on the wood (the bookstand's warmer walnut)   // along: a long spine (the bookstand), grain down its length   // off: where in the photo this block's wood starts (tiles)      // su / sv: metres of wood the photo spans across / up; rect: this block's own footprint
    const m = new THREE.MeshPhysicalMaterial({ side: THREE.DoubleSide, color: 0xffffff, metalness: 0, roughness: 0.6, aoMap: ao || null });
    m.onBeforeCompile = (s) => {
      prep(s, floor, walTop);
      s.uniforms.b3dWal = { value: wal }; s.uniforms.b3dWalRect = rect ? { value: rect } : walRect;
      s.fragmentShader = '#define B3D_WAL_K ' + k.toFixed(3) + '\n#define B3D_WAL_SU ' + su.toFixed(4) + '\n#define B3D_WAL_SV ' + sv.toFixed(4) + '\n#define B3D_WAL_SAT ' + sat.toFixed(3) + '\n#define B3D_WAL_OFF vec2(' + off[0].toFixed(3) + ', ' + off[1].toFixed(3) + ')\n' + (along ? '#define B3D_WAL_ALONG\n' : '') + '#define B3D_WAL_TINT vec3(' + (tint || [1, 1, 1]).map((v) => (+v).toFixed(3)).join(', ') + ')\n' + s.fragmentShader;
      s.fragmentShader = s.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D b3dWal; uniform vec4 b3dWalRect;')
        .replace('#include <color_fragment>', `#include <color_fragment>
          vec3 an = pow(abs(vWorldN), vec3(12.0)); an /= (an.x + an.y + an.z);
          float h = vObj.y - b3dFloor;
          const float SU = B3D_WAL_SU, SV = B3D_WAL_SV;                                              // most of the photo across the front face
          // the sides read as one strip of wood wrapped round the base: u runs along the perimeter (left end,
          // front, right end, back), so the grain carries round every corner; the only join is the back-left corner
          float px = vObj.x - b3dWalRect.x, pz = vObj.z - b3dWalRect.y, HX = b3dWalRect.z, HZ = b3dWalRect.w;
          float sF = 2.0 * HZ + px + HX;                            // front (+z)
          float sB = 4.0 * HZ + 2.0 * HX + (HX - px);               // back (-z)
          float sR = 2.0 * HZ + 2.0 * HX + (HZ - pz);               // right end (+x)
          float sL = pz + HZ;                                       // left end (-x)
          #ifdef B3D_WAL_ALONG
          // a long spine lying along z: the grain runs its full length on the top and both sides; the two ends are
          // end grain, so they read darker
          vec3 cz = texture2D(b3dWal, vec2(vObj.x / SU + 0.5, h / SV + 0.6) + B3D_WAL_OFF).rgb * 0.85;
          vec3 cx = texture2D(b3dWal, vec2(vObj.z / SU + 0.5, h / SV + 0.6) + B3D_WAL_OFF).rgb;
          vec3 cy = texture2D(b3dWal, vec2(vObj.z / SU + 0.5, vObj.x / SV + 0.5) + B3D_WAL_OFF).rgb;
          #else
          vec3 cz = texture2D(b3dWal, vec2((vWorldN.z > 0.0 ? sF : sB) / SU, h / SV + 0.6) + B3D_WAL_OFF).rgb;
          vec3 cx = texture2D(b3dWal, vec2((vWorldN.x > 0.0 ? sR : sL) / SU, h / SV + 0.6) + B3D_WAL_OFF).rgb;
          vec3 cy = texture2D(b3dWal, vec2(vObj.x / SU + 0.5, vObj.z / SV + 0.5) + B3D_WAL_OFF).rgb; // top / bottom
          #endif
          vec3 wood = pow(cz * an.z + cx * an.x + cy * an.y, vec3(2.2));
          float lum = dot(wood, vec3(0.2126, 0.7152, 0.0722));
          float groove = clamp((0.065 - lum) * 15.0, 0.0, 1.0);                       // dark grain lines
          // grooves fade where a pixel covers several texels (far off or at a glancing angle), so they never shimmer into hatching
          float gfade = clamp(1.6 - length(fwidth(vObj)) / SU * 1536.0 * 0.5, 0.0, 1.0);
          wood = mix(vec3(lum), wood, B3D_WAL_SAT) * B3D_WAL_K * B3D_WAL_TINT;                              // his walnut photo, colour as shot   // B3D_WAL_TINT: fitted so walnut / aluminium matches the product photo
          diffuseColor.rgb = wood;`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
          roughnessFactor = roughnessFactor + groove * 0.15 * gfade;`)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
          normal = b3dBump(-vViewPosition, normal, -groove * gfade, 0.000025);`);
    };
    m.customProgramCacheKey = () => 'b3d-walnut-' + k.toFixed(3) + su.toFixed(4) + sv.toFixed(4) + sat.toFixed(3) + off.join() + (along ? 'a' : '') + (tint ? tint.join() : '');
    return m;
  };
  // 3M silicone pad: soft matte black rubber with a fine stipple, a faint sheen at grazing angles
  const silicone = (ao) => {
    const m = new THREE.MeshPhysicalMaterial({ side: THREE.DoubleSide, color: 0x1c1c1d, metalness: 0, roughness: 0.82, aoMap: ao || null, sheen: 0.35, sheenRoughness: 0.6, sheenColor: 0x3a3a3c });
    m.onBeforeCompile = (s) => {
      prep(s, floor);
      s.fragmentShader = s.fragmentShader
        .replace('#include <color_fragment>', `#include <color_fragment>
          float st = b3dNoise(vObj * 9000.0) * 0.6 + b3dNoise(vObj * 2500.0) * 0.4;
          diffuseColor.rgb *= 0.85 + st * 0.3;`)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
          normal = b3dBump(-vViewPosition, normal, st, 0.00003);`);
    };
    m.customProgramCacheKey = () => 'b3d-silicone';
    return m;
  };
  // pressed wool felt: charcoal, a fine fibre speckle over a soft mottle, fuzzy sheen at grazing angles
  // mask: a pattern laser-etched into it (the Trinity tray): the burnt-in shapes sit darker, their fibres shorter and
  // coarser, a hair below the surface
  const felt = (ao, mask, k = 1) => {   // k: tone (the valet trays' felt is darker than the folder lining)
    const m = new THREE.MeshPhysicalMaterial({ side: THREE.DoubleSide, color: new THREE.Color(0x232427).multiplyScalar(k), roughness: 1, metalness: 0, sheen: 0.25, sheenRoughness: 0.8, sheenColor: 0x3a3b3e, envMapIntensity: 0.6, aoMap: ao || null });
    m.onBeforeCompile = (s) => {
      prep(s, floor);
      if (mask) s.uniforms.b3dMask = { value: mask };
      s.fragmentShader = s.fragmentShader
        .replace('#include <common>', '#include <common>' + (mask ? '\nuniform sampler2D b3dMask;' : ''))
        .replace('#include <color_fragment>', `#include <color_fragment>
          float f1 = b3dNoise(vObj * 9000.0), f2 = b3dNoise(vObj * 4200.0 + 13.0), f3 = b3dNoise(vObj * 600.0 + 3.0);
          float fib = f1 * 0.55 + f2 * 0.45;
          float fa = clamp(1.6 - length(fwidth(vObj * 9000.0)) * 0.5, 0.55, 1.0);   // keeps its grain at low angles
          float f4 = b3dNoise(vObj * 1800.0 + 5.0) * 0.6 + b3dNoise(vObj * 3600.0 + 9.0) * 0.4;   // 0.3-0.5 mm tufts, visible from afar
          diffuseColor.rgb *= 0.85 + (fib - 0.5) * 1.3 * fa + (f4 - 0.5) * 0.9 + (f3 - 0.5) * 0.2;
          ${mask ? `float eng = texture2D(b3dMask, vMapUv).r * step(0.9, vWorldN.y);
          float grit = b3dNoise(vObj * 7000.0 + 21.0);
          diffuseColor.rgb *= 1.0 - eng * (0.42 + (grit - 0.5) * 0.35 * fa);` : ''}`)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
          normal = b3dBump(-vViewPosition, normal, fib * fa + f4 * 0.6 ${mask ? '- eng * (2.5 - grit * 0.8)' : ''}, 0.00005);`);
      if (mask) s.fragmentShader = s.fragmentShader.replace('#include <map_fragment>', '');   // the texture is a mask, not a colour
      if (mask) s.fragmentShader = s.fragmentShader.replace('#include <lights_physical_fragment>', '#include <lights_physical_fragment>\n          material.sheenColor *= 1.0 - eng * 0.6;');
    };
    m.customProgramCacheKey = () => 'b3d-felt' + (mask ? '-eng' : '');   // (k only sets the colour uniform)
    if (mask) { m.map = mask; mask.colorSpace = THREE.NoColorSpace; }
    return m;
  };
  return { alu, walnut, felt, silicone };
}

/* ---------- light tent ----------
   The product photos are shot in a white sweep: bright everywhere, a little brighter overhead, the floor
   bouncing light back up, and the camera's side darker (that is what makes the front faces mid-grey and the
   rounded edges bright). This builds that as an environment; it turns with the camera (scene.environmentRotation),
   so every angle is lit like the product shot. */
function lightTent(THREE, renderer, variant) {
  const s = new THREE.Scene();
  // studio == a photo studio built around the camera (it turns with the view): a large soft key overhead and a
  // little to the left, two tall strip lights just behind either side that draw clean highlights down the edges,
  // a white sweep underneath and a darker back wall, so flat faces get a gradient instead of a flat grey
  const studio = `varying vec3 vD;
      #ifndef SWEEP
      #define SWEEP 0.55
      #endif
      #ifndef STRIP
      #define STRIP 1.0
      #endif
      float box(vec3 d, vec3 c, vec3 u, float hw, float hh, float soft) {
        vec3 v = normalize(cross(c, u)); vec3 w = normalize(cross(v, c));
        float k = dot(d, c); if (k <= 0.0) return 0.0;
        vec2 p = vec2(dot(d, v), dot(d, w)) / k;
        vec2 q = abs(p) - vec2(hw, hh);
        return 1.0 - smoothstep(-soft, soft, max(q.x, q.y));
      }
      void main() {
        vec3 d = normalize(vD);
        float L = mix(0.42, 0.62, smoothstep(-0.3, 0.6, d.y)) * ROOM;                                      // room: darker low, lighter high
        L += smoothstep(-0.15, -0.6, d.y) * SWEEP * ROOM;                                                     // white sweep floor
        L += box(d, normalize(KEY_DIR), vec3(0.0, 0.0, 1.0), KEY_W, KEY_H, KEY_SOFT) * KEY_I; // key softbox overhead
        L += box(d, normalize(vec3(0.92, 0.2, -0.25)), vec3(0.0, 1.0, 0.0), 0.10, 0.9, 0.06) * 2.0 * STRIP;   // strip right
        L += box(d, normalize(vec3(-0.92, 0.2, -0.3)), vec3(0.0, 1.0, 0.0), 0.10, 0.9, 0.06) * 1.3 * STRIP;   // strip left
        L += box(d, normalize(vec3(0.0, 0.1, 1.0)), vec3(0.0, 1.0, 0.0), 0.9, 0.35, 0.5) * FILL_I;    // soft fill from the camera
        gl_FragColor = vec4(vec3(L) * vec3(0.985, 0.992, 1.006), 1.0);
      }`;
  const tent = `varying vec3 vD;
      void main() {
        vec3 d = normalize(vD);
        float L = 0.85;
        L += smoothstep(0.55, 0.98, d.y) * 2.2;                 // overhead, tight: top faces bright, rounded edges not
        L += smoothstep(-0.2, -0.9, d.y) * 0.3;                 // white floor bounce
        L *= mix(1.0, 0.8, smoothstep(0.15, 0.85, d.z) * (1.0 - smoothstep(0.55, 0.95, abs(d.y))));   // camera side, a little darker
        L *= mix(1.0, 0.62, smoothstep(0.5, 0.95, abs(d.x)) * (1.0 - smoothstep(0.4, 0.9, abs(d.y))));  // dark flags left and right: edges stay grey
        gl_FragColor = vec4(vec3(L) * vec3(0.976, 0.989, 1.012), 1.0);   // a touch cool
      }`;
  const m = new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false,
    vertexShader: 'varying vec3 vD; void main(){ vD = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    // tray: a smaller, dimmer key set back behind the product, so wide floors sit mid grey and fall off front to back as in
    // the photos; brighter strips and floor sweep put the light on the walls, fillets and rim instead
    fragmentShader: (variant === 'tent' ? tent : (variant === 'tray'
      ? (self.B3D_TRAYENV || '#define KEY_DIR vec3(0.0, 0.8, -0.6)\n#define KEY_W 0.55\n#define KEY_H 0.32\n#define KEY_SOFT 0.7\n#define KEY_I 2.8\n#define FILL_I 1.2\n#define ROOM 0.5\n#define SWEEP 0.7\n#define STRIP 2.5\n')
      : '#define KEY_DIR vec3(-0.25, 0.85, 0.35)\n#define KEY_W 0.95\n#define KEY_H 0.75\n#define KEY_SOFT 0.45\n#define KEY_I 2.6\n#define FILL_I 0.35\n#define ROOM 1.0\n') + studio),
  });
  s.add(new THREE.Mesh(new THREE.SphereGeometry(10, 128, 64), m));
  const tex = new THREE.PMREMGenerator(renderer).fromScene(s, 0.0).texture;
  return tex;
}

/* ---------- eyewear ----------
   A pair of optical frames at real size (metres): black acetate front, clear lenses, gold arms and nose pads.
   Placed as in the product photos: the front leans on the stand's front face (nose pads on the face, bridge a
   little below the top), the arms run back past the sides and the tips hang down behind. */
function buildGlasses(THREE, standBox) {
  const g = new THREE.Group();
  const acetate = new THREE.MeshPhysicalMaterial({ color: 0x080808, roughness: 0.2, clearcoat: 1, clearcoatRoughness: 0.08 });
  const gold = new THREE.MeshStandardMaterial({ color: 0xcfa775, metalness: 1, roughness: 0.28 });
  const glass = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.03, metalness: 0, transparent: true, opacity: 0.14, depthWrite: false, envMapIntensity: 1.4 });
  const rr = (s, w, h, r, cx, cy) => {                      // rounded rect path, centred at cx, cy
    const x = cx - w / 2, y = cy - h / 2;
    s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r); s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
    return s;
  };
  const ext = { depth: 0.0034, bevelEnabled: true, bevelThickness: 0.0006, bevelSize: 0.0006, bevelSegments: 3, curveSegments: 28 };
  const LW = 0.05, LH = 0.041, LR = 0.018, RIM = 0.0034, CX = 0.0355, CY = -0.013;
  for (const sx of [-1, 1]) {
    const ring = rr(new THREE.Shape(), LW + RIM * 2, LH + RIM * 2, LR + RIM, sx * CX, CY);
    ring.holes.push(rr(new THREE.Path(), LW, LH, LR, sx * CX, CY));
    const rim = new THREE.Mesh(new THREE.ExtrudeGeometry(ring, ext), acetate); rim.position.z = -0.0017; g.add(rim);
    const lens = new THREE.Mesh(new THREE.ShapeGeometry(rr(new THREE.Shape(), LW + 0.0006, LH + 0.0006, LR, sx * CX, CY), 28), glass); g.add(lens);
    // end piece (hinge block) and nose pad
    const end = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.0045, 0.0055), gold); end.position.set(sx * (CX + LW / 2 + RIM - 0.0004), CY + LH / 2 - 0.004, -0.0042); g.add(end);
    const pad = new THREE.Mesh(new THREE.SphereGeometry(0.0034, 16, 12), gold); pad.scale.set(0.55, 1, 0.35); pad.position.set(sx * 0.0085, CY + 0.004, -0.0048); g.add(pad);
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.0005, 0.0005, 0.007, 8), gold); arm.position.set(sx * 0.0095, CY + 0.008, -0.003); arm.rotation.x = 1.0; g.add(arm);
    // arm (temple): back from the hinge, a little inward, then down over the ear
    const hx = sx * (CX + LW / 2 + RIM - 0.0004), hy = CY + LH / 2 - 0.004;
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(hx, hy, -0.005), new THREE.Vector3(sx * 0.064, hy + 0.004, -0.05), new THREE.Vector3(sx * 0.062, hy + 0.006, -0.1),
      new THREE.Vector3(sx * 0.061, hy - 0.004, -0.122), new THREE.Vector3(sx * 0.06, hy - 0.024, -0.135),
    ]);
    g.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 64, 0.0011, 10), gold));
    const tipCurve = new THREE.CatmullRomCurve3(curve.getPoints(64).slice(-22));
    g.add(new THREE.Mesh(new THREE.TubeGeometry(tipCurve, 24, 0.0021, 10), acetate));
  }
  const bridge = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3([
    new THREE.Vector3(-0.0115, 0.0035, 0), new THREE.Vector3(0, 0.0065, 0), new THREE.Vector3(0.0115, 0.0035, 0)]), 20, 0.0012, 10), gold);
  g.add(bridge);
  // rest on the stand: the front parallel to the stand's sloped front face, a pad's depth in front of it
  const yTop = standBox.max.y, zTop = standBox.max.z * 0.61, yLow = standBox.min.y + 0.0065, zLow = standBox.max.z;
  const slope = Math.atan2(zLow - zTop, yTop - yLow);
  const by = yTop - 0.008, bz = zLow - (by - yLow) / (yTop - yLow) * (zLow - zTop) + 0.0062;
  g.position.set(0, by, bz); g.rotation.set(-slope, 0, 0);
  g.traverse((o) => { if (o.isMesh) o.renderOrder = o.material === glass ? 2 : 0; });
  return g;
}

/* ---------- one viewer ---------- */
async function mountViewer(stage) {
  const { THREE, GLTFLoader, DRACOLoader } = await loadLibs();

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.domElement.className = 'b3d-canvas';
  stage.prepend(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(20, 1, 0.002, 20);
  const [gltf, walTex] = await Promise.all([
    new GLTFLoader().setDRACOLoader(new DRACOLoader().setDecoderPath(THREE_URL + 'examples/jsm/libs/draco/')).loadAsync(stage.dataset.src),
    stage.dataset.walnut ? new THREE.TextureLoader().loadAsync(stage.dataset.walnut) : Promise.resolve(null),
  ]);
  if (walTex) { walTex.wrapS = walTex.wrapT = THREE.RepeatWrapping; walTex.anisotropy = 8; }
  scene.environment = lightTent(THREE, renderer, stage.dataset.env || 'studio');
  if (stage.dataset.plain) {                                               // test: AR model under a Quick Look-like room light
    const { RoomEnvironment } = await import(THREE_URL + 'examples/jsm/environments/RoomEnvironment.js/+esm');
    scene.environment = new THREE.PMREMGenerator(renderer).fromScene(new RoomEnvironment(), 0.04).texture;
  }

  const model = gltf.scene; scene.add(model);
  const box = new THREE.Box3().setFromObject(model), size = box.getSize(new THREE.Vector3()), center = box.getCenter(new THREE.Vector3());
  const radius = size.length() / 2, floor = { value: box.min.y }, walTop = { value: -1 };
  model.traverse((o) => { if (o.isMesh && (o.material.name || '').startsWith('walnut')) { o.geometry.computeBoundingBox(); walTop.value = Math.max(walTop.value, o.geometry.boundingBox.max.y); } });
  // the warm bounce off the walnut only makes sense when the walnut is a base under the metal (the eyewear stand),
  // not an inlay set into its top (the ruler)
  let aluLow = Infinity;
  model.traverse((o) => { if (o.isMesh && (o.material.name || '').startsWith('alu')) { o.geometry.computeBoundingBox(); aluLow = Math.min(aluLow, o.geometry.boundingBox.min.y); } });
  if (walTop.value > aluLow + 0.001) walTop.value = -1;
  const walRect = { value: new THREE.Vector4(0, 0, 0.03, 0.02) };
  model.traverse((o) => { if (o.isMesh && (o.material.name || '').startsWith('walnut')) { const b = o.geometry.boundingBox; walRect.value.set((b.min.x + b.max.x) / 2, (b.min.z + b.max.z) / 2, (b.max.x - b.min.x) / 2, (b.max.z - b.min.z) / 2); } });
  const mats = makeMaterials(THREE, floor, walTop, walRect, stage.dataset.finish);   // data-finish="brushed": the folder's hand-brushed sheet
  const coarseAlu = size.y < 0.2 * Math.min(size.x, size.z) && !(size.x > 4 * Math.max(size.y, size.z));   // trays
  if (coarseAlu && !stage.dataset.env) {
    // trays: wide flat faces all average the same grey under a blurred environment, so a real key light rides with
    // the camera (above-left, in front) and faces turned toward it or away from it separate as in the photos
    scene.environment = lightTent(THREE, renderer, 'tray'); renderer.toneMappingExposure = +(stage.dataset.exposure || 1.3);
    const key = new THREE.DirectionalLight(0xffffff, +(stage.dataset.key || 2.5)); key.position.set(-0.55, 1.0, 1.0); key.target.position.set(0, 0, -1);
    camera.add(key); camera.add(key.target); scene.add(camera);
  }
  let wi = 0;   // walnut block counter
  if (!stage.dataset.plain) model.traverse((o) => {                       // data-plain: keep the GLB's own PBR materials
    if (!o.isMesh) return;
    const old = o.material, name = old.name || '', ao = old.aoMap || null; o.userData.mat = name;
    if (name.startsWith('alu_etch')) o.material = mats.alu(ao, null, true, coarseAlu);       // laser-etched marks
    else if (name === 'alu_engraved') o.material = mats.alu(ao, old.map, false, coarseAlu);
    else if (name.startsWith('alu')) o.material = mats.alu(ao, null, false, coarseAlu);
    else if (name.startsWith('walnut')) {
      // data-walnut-blocks: several separate walnut blocks (the monitor stand), each wrapped by its own footprint
      let rect = null, off = [0, 0];
      if (stage.dataset.walnutBlocks) { off = [0, [-0.55, -0.05, -0.3, 0.15][wi % 4]]; wi++;   // the photo wraps exactly once round each block (scale = perimeter); each block takes its own band of it, so no two match and nothing repeats
        o.geometry.computeBoundingBox(); const b = o.geometry.boundingBox; rect = new THREE.Vector4((b.min.x + b.max.x) / 2, (b.min.z + b.max.z) / 2, (b.max.x - b.min.x) / 2, (b.max.z - b.min.z) / 2);
        // the photo's one join goes in the middle of the block's inner end (the face toward the other leg, mostly behind the shelf)
        const P = 4 * (rect.z + rect.w), sSeam = rect.x > 0 ? rect.w : 3 * rect.w + 2 * rect.z; off[0] = 1 - sSeam / P; rect.perim = P; }
      const su = stage.dataset.walnutScale ? +stage.dataset.walnutScale : rect && rect.perim ? rect.perim : 0.09;   // an explicit scale wins over one wrap per block
      o.material = mats.walnut(ao, walTex, +(stage.dataset.walnutGain || 1.3), su, +(stage.dataset.walnutSv || su / 1.5), rect, +(stage.dataset.walnutSat || 0.72), off, !!stage.dataset.walnutAlong, stage.dataset.walnutTint ? stage.dataset.walnutTint.split(',').map(Number) : null);   // data-walnut-along: the bookstand spine
    }
    else if (name.startsWith('felt')) o.material = mats.felt(ao, old.map, coarseAlu ? +(self.B3D_FELT || 0.6) : 1);
    else if (name.startsWith('silicone')) o.material = mats.silicone(ao);
  });

  // dimensions: blue measuring lines along three edges, labels in mm and inches
  const dims = new THREE.Group(); dims.visible = false; scene.add(dims);
  const lineMat = new THREE.LineBasicMaterial({ color: BLUE, depthTest: false, transparent: true });
  const gap = radius * 0.12, tick = radius * 0.05, labels = [];
  const fmt = (m) => { const mm = m * 1000, inch = mm / 25.4; return `${Math.round(mm)} mm | ${(Math.round(inch * 20) / 20).toFixed(2)}"`; };
  const addDim = (a, b, off, tickDir, len) => {
    const A = a.clone().add(off), B = b.clone().add(off), t = tickDir.clone().multiplyScalar(tick);
    const l = new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints([A, B, A.clone().sub(t), A.clone().add(t), B.clone().sub(t), B.clone().add(t)]), lineMat);
    l.renderOrder = 10; dims.add(l);
    const el = document.createElement('span'); el.className = 'b3d-dim'; el.textContent = fmt(len); stage.querySelector('.b3d-dims').append(el);
    labels.push({ el, at: A.clone().lerp(B, 0.5) });
  };
  const { min, max } = box;
  addDim(new THREE.Vector3(min.x, min.y, max.z), new THREE.Vector3(max.x, min.y, max.z), new THREE.Vector3(0, 0, gap), new THREE.Vector3(0, 0, 1), size.x);
  addDim(new THREE.Vector3(max.x, min.y, max.z), new THREE.Vector3(max.x, min.y, min.z), new THREE.Vector3(gap, 0, 0), new THREE.Vector3(1, 0, 0), size.z);
  addDim(new THREE.Vector3(min.x, min.y, max.z), new THREE.Vector3(min.x, max.y, max.z), new THREE.Vector3(-gap, 0, 0), new THREE.Vector3(1, 0, 0), size.y);

  // feature dots
  let spots = [];
  try { spots = JSON.parse(stage.dataset.hotspots || '[]') || []; } catch (e) { spots = []; }
  const dotLayer = stage.querySelector('.b3d-dots'), note = stage.querySelector('.b3d-note');
  spots = spots.filter((h) => h && h.p && h.title).map((h, i) => {
    const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'b3d-dot'; btn.setAttribute('aria-label', h.title);
    dotLayer && dotLayer.append(btn);
    const P = new THREE.Vector3(...h.p), N = new THREE.Vector3(...(h.n || [0, 1, 0])).normalize();
    const D = h.out ? new THREE.Vector3(...h.out).normalize() : null;   // optional: the way the dot leaves the product, if not along N
    return { ...h, i, btn, P, N, D };
  });
  // pin each feature to the real surface: cast in from just outside along its normal and take the hit point and
  // the face's own normal, so a hand-typed position can never sit inside or off the part
  {
    model.updateMatrixWorld(true); const ray = new THREE.Raycaster(), parts = []; model.traverse((o) => { if (o.isMesh) parts.push(o); });
    spots.forEach((s) => {
      ray.set(s.P.clone().addScaledVector(s.N, 0.03), s.N.clone().negate()); ray.far = 0.06;
      const hit = ray.intersectObjects(parts, false)[0];
      if (hit && hit.point.distanceTo(s.P) < 0.012) {
        s.P.copy(hit.point);
        if (hit.face) { const fn = hit.face.normal.clone().transformDirection(hit.object.matrixWorld); if (fn.dot(s.N) > 0.3) s.N.copy(fn); }
      }
    });
    // the dots ride on a shell around the product, about a third of its middle dimension out (min 8 mm), so they
    // read as beside it rather than on it
    const pb = new THREE.Box3().setFromObject(model), ps = pb.getSize(new THREE.Vector3()), mid = [ps.x, ps.y, ps.z].sort((a, b) => a - b)[1];
    const out = pb.clone().expandByScalar(Math.max(0.008, mid * 0.33));
    spots.forEach((s) => {
      const dir = s.D || s.N;
      let t = Infinity;                                           // distance along dir to leave the expanded box
      for (const ax of ['x', 'y', 'z']) {
        const d = dir[ax];
        if (d > 1e-4) t = Math.min(t, (out.max[ax] - s.P[ax]) / d); else if (d < -1e-4) t = Math.min(t, (out.min[ax] - s.P[ax]) / d);
      }
      s.A = s.P.clone().addScaledVector(dir, Number.isFinite(t) ? Math.max(t, 0.008) : 0.008);
    });
  }
  let active = null;

  // camera rig: orbit (azimuth / elevation) round a target, distance = fitted distance x zoom.
  // Everything eases: hands and buttons move a goal, and the camera glides after it every frame
  // (frame-rate independent), so turns settle softly instead of stopping dead.
  // long, flat things (the ruler) start from higher up with the length across the frame and closer in (the fit is
  // for every angle, so a 30 cm ruler would otherwise rest as a sliver), and can be brought right up to the scale
  const LONG = size.x > 4 * Math.max(size.y, size.z);
  const FLAT = !LONG && size.y < 0.2 * Math.min(size.x, size.z);   // trays: low and wide, start closer and from higher up
  // data-start="az,el,zoom": a product's own resting view (the desktop folder: straight on, like the 📁 emoji)
  const START = (stage.dataset.start || '').split(',').filter(Boolean).map(Number);
  const V = START.length >= 2 ? { ...VIEWS, '3/4': [START[0], START[1]] } : LONG ? { ...VIEWS, '3/4': [12, 38] } : FLAT ? { ...VIEWS, '3/4': [28, 34] } : VIEWS, home = START[2] ? START[2] * (stage.clientWidth / stage.clientHeight < 1.2 ? 1.35 : 1) : LONG ? 0.62 : FLAT ? 0.8 : stage.closest('[data-b3d-sheet]') && stage.clientHeight > stage.clientWidth ? 1.45 : HOME,   // a tall full-screen sheet starts closer
    zMin = LONG ? 0.07 : ZOOM_MIN;
  const T0 = START.length >= 6 ? new THREE.Vector3(START[3], START[4], START[5]) : center;   // data-start can also aim the camera
  const rig = { az: V['3/4'][0], el: V['3/4'][1], zoom: home, target: T0.clone() };
  const goal = { az: rig.az, el: rig.el, zoom: home, target: T0.clone() };
  let base = 1, baseGoal = 1, rate = 9, vel = 0, frame = 0, last = 0;
  const dirOf = (az, el) => { const a = THREE.MathUtils.degToRad(az), e = THREE.MathUtils.degToRad(el); return new THREE.Vector3(Math.cos(e) * Math.sin(a), Math.sin(e), Math.cos(e) * Math.cos(a)); };
  const upOf = (az, el) => (Math.abs(el) > 85 ? new THREE.Vector3(-Math.sin(THREE.MathUtils.degToRad(az)) * Math.sign(el), 0, -Math.cos(THREE.MathUtils.degToRad(az)) * Math.sign(el)) : new THREE.Vector3(0, 1, 0));
  // one fitted distance for every angle (the whole thing fits whichever way it is turned), so turning never zooms
  const fitBox = (bx, wide) => {
    const cs = []; for (let i = 0; i < 8; i++) cs.push(new THREE.Vector3(i & 1 ? bx.max.x : bx.min.x, i & 2 ? bx.max.y : bx.min.y, i & 4 ? bx.max.z : bx.min.z));
    const tv = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2), th = tv * camera.aspect, fill = wide ? 0.86 : 0.7;
    let need = 0; const m = new THREE.Matrix4(), q = new THREE.Vector3();
    for (let el = wide ? 0 : -80; el <= (wide ? 40 : 80); el += 20) for (let az = wide ? -40 : 0; az < (wide ? 41 : 360); az += wide ? 20 : 30) {
      m.lookAt(dirOf(az, el), new THREE.Vector3(), upOf(az, el)).invert();
      for (const c of cs) { q.copy(c).sub(center).applyMatrix4(m); need = Math.max(need, Math.abs(q.x) / (th * fill) + q.z, Math.abs(q.y) / (tv * fill) + q.z); }
    }
    return need;
  };
  const place = () => {
    camera.position.copy(rig.target).addScaledVector(dirOf(rig.az, rig.el), base * rig.zoom);
    camera.up.copy(upOf(rig.az, rig.el)); camera.lookAt(rig.target);
    scene.environmentRotation.copy(camera.rotation);           // the tent turns with the camera
  };
  const tv = new THREE.Vector3(), cp = new THREE.Vector3(), PIN_LIFT = 0.003;
  const occRay = new THREE.Raycaster(), occParts = []; model.traverse((o) => { if (o.isMesh) occParts.push(o); });
  // the open feature's note sits to the left of the product, level with it; where there's no room (phones) it
  // stays top-left
  const outline = { x0: 0, cy: 0 };
  const placeNote = () => {
    if (!note || note.hidden) return;
    const x = outline.x0 - 56 - note.offsetWidth, y = Math.max(16, outline.cy - note.offsetHeight / 2);
    const beside = x > 20; note.classList.toggle('is-beside', beside);
    note.style.transform = beside ? `translate(${x}px, ${y}px)` : '';
  };
  const draw = (now) => {
    frame = 0;
    const dt = Math.min(0.05, last ? (now - last) / 1000 : 1 / 60); last = now;
    if (!pointers.size && Math.abs(vel) > 0.5) { goal.az += vel * dt; vel *= Math.exp(-dt * 3.2); } else if (!pointers.size) vel = 0;
    const k = 1 - Math.exp(-dt * rate), kb = 1 - Math.exp(-dt * 5);
    rig.az += (goal.az - rig.az) * k; rig.el += (goal.el - rig.el) * k; rig.zoom += (goal.zoom - rig.zoom) * k;
    rig.target.lerp(goal.target, k); base += (baseGoal - base) * kb;
    const glassesMoving = stepGlasses(now), feltMoving = stepFelt(dt) || stepPart(dt), asmMoving = stepAssembly(dt);
    place(); renderer.render(scene, camera);
    const w = stage.clientWidth, h = stage.clientHeight;
    if (dims.visible) labels.forEach(({ el, at }) => { tv.copy(at).project(camera); el.style.transform = `translate(${(tv.x * 0.5 + 0.5) * w}px, ${(-tv.y * 0.5 + 0.5) * h}px) translate(-50%, -50%)`; });
    // the product's outline on screen: the open note sits to its left
    let rx0 = 1e9;
    for (let i = 0; i < 8; i++) {
      tv.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).project(camera);
      rx0 = Math.min(rx0, (tv.x * 0.5 + 0.5) * w);
    }
    tv.copy(center).project(camera); outline.x0 = rx0; outline.cy = (-tv.y * 0.5 + 0.5) * h; placeNote();
    // each dot hangs off the product: from its feature straight out along the face's normal to the shell round the
    // product (s.A, fixed in 3D), so it sits beside the part, turns with it and never slides around. A feature facing away stays as a fainter dot, still clickable: a tap turns the product round to face it.
    spots.forEach((s) => {
      tv.copy(s.A);
      cp.copy(camera.position).sub(tv).normalize();
      let face = s.N.dot(cp);
      // hidden behind another part of the product (the channel seen past the front wall): treat as facing away
      if (face > -0.15) {
        const d = camera.position.distanceTo(tv);
        occRay.set(camera.position, cp.clone().negate()); occRay.far = d - 0.0015;
        if (occRay.intersectObjects(occParts, false).length) face = -1;
      }
      tv.project(camera);
      const x = (tv.x * 0.5 + 0.5) * w, y = (-tv.y * 0.5 + 0.5) * h;
      const front = THREE.MathUtils.smoothstep(face, -0.15, 0.2);
      const show = tv.z < 1 && !glassesOn;
      s.btn.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      s.btn.style.opacity = show ? (0.4 + 0.6 * front).toFixed(3) : '0';
      s.btn.classList.toggle('is-hidden', !show);
    });
    const settling = Math.abs(goal.az - rig.az) > 0.01 || Math.abs(goal.el - rig.el) > 0.01 || Math.abs(goal.zoom - rig.zoom) > 0.0005
      || rig.target.distanceTo(goal.target) > 1e-6 || Math.abs(baseGoal - base) > 1e-5 || Math.abs(vel) > 0.5;
    if (settling || glassesMoving || feltMoving || asmMoving) request(); else last = 0;
  };
  const request = () => { if (!frame) frame = requestAnimationFrame(draw); };
  const goTo = (b, r = 5) => {
    // shortest way round, then glide there
    let az1 = b.az ?? goal.az; const d = ((az1 - rig.az) % 360 + 540) % 360 - 180; az1 = rig.az + d;
    goal.az = az1; goal.el = b.el ?? goal.el; goal.zoom = b.zoom ?? goal.zoom; if (b.target) goal.target.copy(b.target);
    vel = 0; rate = r; request();
  };

  // eyewear: a pair of glasses (built here) eases down onto the stand and rests as in the product photos:
  // nose pads on the front face, bridge just below the top, arms open over the sides, tips hanging behind
  const glasses = buildGlasses(THREE, box);
  glasses.visible = false; scene.add(glasses);
  const restPos = glasses.position.clone(), restRot = glasses.rotation.clone();
  const glassesBox = new THREE.Box3().setFromObject(glasses).union(box);
  let glassesOn = false, gAnim = null;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3), easeIn = (t) => t * t * t;
  function stepGlasses(now) {
    if (!gAnim) return false;
    const t = Math.min(1, (now - gAnim.t0) / gAnim.dur);
    // in: from above and in front, a little tilted, settling onto the stand; out: lifts away the same way
    const e = gAnim.in ? easeOut(t) : 1 - easeIn(t);
    glasses.position.set(restPos.x, restPos.y + (1 - e) * 0.07, restPos.z + (1 - e) * 0.05);
    glasses.rotation.set(restRot.x - (1 - e) * 0.35, restRot.y + (1 - e) * 0.25, restRot.z + (1 - e) * 0.08);
    glasses.visible = gAnim.in || t < 1;
    if (t >= 1) { gAnim = null; return false; }
    return true;
  }
  const eyeBtn = stage.querySelector('[data-eyewear]');
  const setGlasses = (on) => {
    glassesOn = on; eyeBtn && eyeBtn.classList.toggle('is-active', on);
    gAnim = { t0: performance.now(), dur: on ? 1400 : 700, in: on };
    glasses.visible = true; baseGoal = on ? fitBox(glassesBox, true) : fitBox(box);
    if (on) { closeNote(); goTo({ az: 28, el: 16, zoom: 1.15, target: center }, 3.5); }
    request();
  };
  if (eyeBtn) eyeBtn.addEventListener('click', () => setGlasses(!glassesOn));

  const closeNote = () => { partGoal = 0; stage.classList.remove('is-lifting'); active = null; spots.forEach((s) => s.btn.classList.remove('is-active')); if (note) note.hidden = true; stage.classList.remove('has-note'); };
  const view = (name) => { closeNote(); const [az, el] = V[name]; goTo({ az, el, zoom: home, target: name === '3/4' ? T0 : center }); };

  // feature: turn to face it, move in, show its note
  spots.forEach((s) => s.btn.addEventListener('click', () => {
    if (active === s) { view('3/4'); return; }
    active = s; spots.forEach((x) => x.btn.classList.toggle('is-active', x === s));
    let az = goal.az, el;
    if (Math.abs(s.N.y) > 0.9) el = 62 * Math.sign(s.N.y);
    else { az = THREE.MathUtils.radToDeg(Math.atan2(s.N.x, s.N.z)); el = THREE.MathUtils.clamp(THREE.MathUtils.radToDeg(Math.asin(s.N.y)) + 14, -45, 60); }
    if (s.felt && !feltOn && feltBtn && !feltBtn.hidden) feltBtn.click();   // the felt note puts the pads in if they are out
    showPart(s.lift);   // a note can lift one part out and fade the rest (the ruler's walnut)
    if (s.view) { az = s.view.az ?? az; el = s.view.el ?? el; }   // a note can fix its own angle (the folder's emoji note: the start angle)
    goTo({ az, el, zoom: s.view?.zoom ?? (LONG ? 0.22 : 1.15), target: s.view ? (s.view.target ? new THREE.Vector3(...s.view.target) : center.clone()) : center.clone().lerp(s.P, LONG ? 0.85 : 0.3) }, 4);   // a fixed view brings its own aim
    if (note) { const ix = note.querySelector('.b3d-note-index'), pad = (n) => String(n).padStart(2, '0'); if (ix) ix.textContent = `${pad(spots.indexOf(s) + 1)} / ${pad(spots.length)}`; note.querySelector('.b3d-note-title').textContent = s.title; note.querySelector('.b3d-note-text').textContent = s.text || ''; note.hidden = false; stage.classList.add('has-note'); placeNote(); }
  }));
  if (note) note.querySelector('[data-note-close]')?.addEventListener('click', () => view('3/4'));

  // hands: drag turns (mouse: both axes; one finger: round only, so vertical swipes still scroll the page),
  // two fingers pinch to zoom and slide up / down to tilt; a trackpad pinch or cmd / ctrl + scroll zooms on desktop.
  // A flick keeps it turning and slows to a stop.
  const cv = renderer.domElement, pointers = new Map(); let pinch = null, track = [], lastTap = null;
  const zoomBy = (f) => { goal.zoom = THREE.MathUtils.clamp(goal.zoom * f, zMin, ZOOM_MAX); rate = 10; request(); };
  cv.addEventListener('pointerdown', (e) => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, type: e.pointerType }); cv.setPointerCapture(e.pointerId);
    vel = 0; rate = 14; track = []; stage.classList.add('is-touched', 'is-engaged');
    if (pointers.size === 2) { const [a, b] = [...pointers.values()]; pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), y: (a.y + b.y) / 2 }; }
  });
  cv.addEventListener('pointermove', (e) => {
    const p = pointers.get(e.pointerId); if (!p) return;
    const dx = e.clientX - p.x, dy = e.clientY - p.y; p.x = e.clientX; p.y = e.clientY;
    const k = 240 / Math.max(stage.clientWidth, 1);
    if (pointers.size === 1) {
      goal.az -= dx * k;
      track.push({ t: performance.now(), az: goal.az }); if (track.length > 6) track.shift();
      if (p.type !== 'touch' || stage.closest('[data-b3d-sheet]')) goal.el = THREE.MathUtils.clamp(goal.el + dy * k, EL_MIN, EL_MAX);   // full screen: one finger tilts too
    } else if (pointers.size === 2 && pinch) {
      const [a, b] = [...pointers.values()], d = Math.hypot(a.x - b.x, a.y - b.y), y = (a.y + b.y) / 2;
      goal.zoom = THREE.MathUtils.clamp(goal.zoom * (pinch.d / Math.max(d, 1)), zMin, ZOOM_MAX);
      goal.el = THREE.MathUtils.clamp(goal.el + (y - pinch.y) * k, EL_MIN, EL_MAX);
      pinch = { d, y };
    }
    rate = 14; request();
  });
  const up = (e) => {
    const p = pointers.get(e.pointerId); if (!p) return;
    const tap = pointers.size === 1 && Math.hypot(e.clientX - p.x0, e.clientY - p.y0) < 6;
    pointers.delete(e.pointerId); pinch = null;
    // a double click / double tap glides back to the opening view (and closes any note)
    if (tap) { const t = performance.now(), dbl = lastTap && t - lastTap.t < 350 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30; lastTap = dbl ? null : { t, x: e.clientX, y: e.clientY }; if (dbl) { vel = 0; track = []; view('3/4'); return; } }
    if (tap && active) { view('3/4'); return; }                    // a click on empty space closes the open feature
    if (!pointers.size && track.length > 1) {
      // release speed from the last ~80 ms of the drag (degrees per second), capped
      const a = track[0], b = track[track.length - 1], dt = (b.t - a.t) / 1000;
      vel = performance.now() - b.t < 80 && dt > 0 ? THREE.MathUtils.clamp((b.az - a.az) / dt, -500, 500) : 0;
      request();
    }
  };
  cv.addEventListener('pointerup', up); cv.addEventListener('pointercancel', up);
  stage.addEventListener('pointerleave', () => stage.classList.remove('is-engaged'));
  cv.addEventListener('wheel', (e) => {
    // in the full-screen sheet scrolling zooms; inline, scrolling scrolls the page and only a trackpad pinch (sent as
    // ctrl + wheel) or cmd / ctrl + scroll zooms
    if (!stage.closest('[data-b3d-sheet]') && !e.ctrlKey && !e.metaKey) return;
    e.preventDefault(); zoomBy(Math.exp(e.deltaY * (e.ctrlKey && Math.abs(e.deltaY) < 50 ? 0.01 : 0.0015)));   // pinch deltas are small, so they get more gain
  }, { passive: false });

  // removable felt pads (the valet tray): the button lifts them up out of the tray and fades them, or sets them back
  // data-felt="fixed": the felt is part of the product (the folder's lining), always shown, no button
  // one part lifted out of the product with everything else faded away, e.g. the ruler's walnut insert: a note sets
  // "lift": {"part": "walnut", "by": 0.012} (metres up); Back or another note sets it down and brings the rest back
  let partGoal = 0, partT = 0, lifted = [], faded = [], liftBy = 0, liftFade;
  function showPart(cfg) {
    stage.classList.toggle('is-lifting', !!cfg);
    if (!cfg) { partGoal = 0; request(); return; }
    lifted = []; faded = [];
    model.traverse((o) => { if (!o.isMesh) return; ((o.userData.mat || '').startsWith(cfg.part) ? lifted : faded).push(o); });
    lifted.forEach((o) => { if (o.userData.py0 === undefined) o.userData.py0 = o.position.y; });
    faded.forEach((o) => { if (!o.material.transparent) { o.material.transparent = true; o.material.needsUpdate = true; } });   // (these parts never share a material with the lifted one)
    liftBy = cfg.by || 0.012; liftFade = cfg.fade; partGoal = 1; request();
  }
  function stepPart(dt) {
    if (!lifted.length) return false;
    partT += (partGoal - partT) * (1 - Math.exp(-dt * 6));
    if (Math.abs(partGoal - partT) < 0.002) partT = partGoal;
    const e = partT * partT * (3 - 2 * partT);
    // its baked shading assumed it sat inside the product (sides shut in, so black): it fades as the part comes out
    lifted.forEach((o) => { o.position.y = o.userData.py0 + liftBy * e; if (o.material.aoMap) { o.userData.ao0 ??= o.material.aoMapIntensity; o.material.aoMapIntensity = o.userData.ao0 * (1 - e); } });
    faded.forEach((o) => { o.material.opacity = 1 - e * (1 - (liftFade ?? 0.18)); o.material.depthWrite = e < 0.02; });   // the rest stays as a faint ghost
    return partT !== partGoal;
  }
  const felts = []; if (stage.dataset.felt !== 'fixed') model.traverse((o) => { if (o.isMesh && (o.userData.mat || '').startsWith('felt')) felts.push(o); });
  const feltBtn = stage.querySelector('[data-felt]');
  let feltOn = stage.dataset.felt !== 'off', feltT = feltOn ? 1 : 0;
  felts.forEach((o) => { o.userData.y0 = o.position.y; o.material.transparent = true; });
  function stepFelt(dt) {
    if (!felts.length) return false;
    const goalT = feltOn ? 1 : 0; feltT += (goalT - feltT) * (1 - Math.exp(-dt * 7));
    if (Math.abs(goalT - feltT) < 0.002) feltT = goalT;
    const e = feltT * feltT * (3 - 2 * feltT);
    felts.forEach((o) => {
      o.position.y = o.userData.y0 + (1 - e) * 0.03; o.material.opacity = e; o.visible = e > 0.01;
      o.material.depthWrite = e > 0.98;
    });
    return feltT !== goalT;
  }
  if (feltBtn) {
    feltBtn.hidden = !felts.length;
    feltBtn.classList.toggle('is-active', feltOn);
    feltBtn.addEventListener('click', () => { feltOn = !feltOn; feltBtn.classList.toggle('is-active', feltOn); request(); });
  }
  stepFelt(1);

  // assembly (the bookstand): the button slides the parts apart along one axis, in order out from the middle, and back
  // together. data-assembly="x,20,14;alu_screw=0.24;alu_sleeve=0.035;alu=0.07": the axis and the angle (azimuth,
  // elevation) the camera eases to, then how far each part moves out (m), by the start of its material name (first
  // match wins); parts on the centre line or with no rule (the walnut spine) stay put
  const asmBtn = stage.querySelector('[data-assembly]'), asmSpec = (stage.dataset.assembly || '').split(';'), asmHead = asmSpec[0].split(','), asmAx = asmHead[0], asmParts = [];
  const asmRules = asmSpec.slice(1).map((r) => r.split('=')).filter((r) => r.length === 2).map(([k, v]) => [k.trim(), +v]);
  let asmOn = false, asmT = 0, asmBack = null;
  if (asmAx && 'xyz'.includes(asmAx)) model.traverse((o) => {
    if (!o.isMesh) return;
    const rule = asmRules.find(([k]) => (o.userData.mat || '').startsWith(k)); if (!rule) return;
    const b = new THREE.Box3().setFromObject(o), c = (b.min[asmAx] + b.max[asmAx]) / 2 - center[asmAx];
    if (Math.abs(c) < 0.005) return;
    asmParts.push({ o, p0: o.position[asmAx], d: Math.sign(c) * rule[1] });
  });
  function stepAssembly(dt) {
    if (!asmParts.length) return false;
    const goalT = asmOn ? 1 : 0; asmT += (goalT - asmT) * (1 - Math.exp(-dt * 4));
    if (Math.abs(goalT - asmT) < 0.002) asmT = goalT;
    const e = asmT * asmT * (3 - 2 * asmT);
    asmParts.forEach(({ o, p0, d }) => { o.position[asmAx] = p0 + d * e; });
    return asmT !== goalT;
  }
  if (asmBtn) {
    asmBtn.hidden = !asmParts.length;
    asmBtn.addEventListener('click', () => {
      asmOn = !asmOn; asmBtn.classList.toggle('is-active', asmOn); stage.classList.toggle('is-exploded', asmOn);
      if (asmOn) { closeNote(); if (dims.visible && dimBtn) dimBtn.click(); }   // the dots and measurements belong to the assembled stand
      goal.zoom *= asmOn ? 1.12 : 1 / 1.12;                                       // step back so the parts stay in frame
      if (asmOn && asmHead[1]) {                                                  // the nearest turn to the exploded angle, and back after
        asmBack = { az: goal.az, el: goal.el };
        goal.az += ((((+asmHead[1] - goal.az) % 360) + 540) % 360) - 180; goal.el = +(asmHead[2] || goal.el);
      } else if (!asmOn && asmBack) { goal.az = asmBack.az; goal.el = asmBack.el; asmBack = null; }
      request();
    });
  }

  const dimBtn = stage.querySelector('[data-dims]');
  if (dimBtn) dimBtn.addEventListener('click', () => {
    dims.visible = !dims.visible; dimBtn.classList.toggle('is-active', dims.visible); stage.classList.toggle('show-dims', dims.visible); request();
  });

  const resize = () => {
    const w = stage.clientWidth, h = stage.clientHeight; if (!w || !h) return;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix(); base = baseGoal = glassesOn ? fitBox(glassesBox, true) : fitBox(box); request();
  };
  new ResizeObserver(resize).observe(stage); resize();
  stage.classList.add('is-ready');
  stage.b3d = { V3: THREE.Vector3, view, goTo, toggleDims: () => dimBtn && dimBtn.click(), spots, request, setGlasses, camera: () => ({ az: +rig.az.toFixed(1), el: +rig.el.toFixed(1), zoom: +rig.zoom.toFixed(3), target: rig.target.toArray().map((v) => +v.toFixed(4)) }) };
}

/* ---------- AR: place the real-size product in the room ----------
   iPhone / iPad: Safari opens a rel="ar" link to a .usdz in Quick Look.
   Android: Chrome hands the plain .glb to Google's Scene Viewer through an intent link.
   Computers: a QR code that opens this page on a phone. None of it needs three.js. */
const IOS_AR = (() => { const a = document.createElement('a'); return !!(a.relList && a.relList.supports && a.relList.supports('ar')); })();
const ANDROID = /android/i.test(navigator.userAgent) && !/firefox/i.test(navigator.userAgent);
function setupAR(stage) {
  const btn = stage.querySelector('[data-ar]'); if (!btn) return;
  const usdz = stage.dataset.arUsdz, glb = stage.dataset.arGlb;
  const abs = (u) => new URL(u, location.href).href;
  if (IOS_AR && usdz) {
    btn.hidden = false;
    btn.addEventListener('click', () => {
      const a = document.createElement('a'); a.rel = 'ar'; a.href = abs(usdz) + '#allowsContentScaling=0';
      a.appendChild(document.createElement('img')); document.body.appendChild(a); a.click(); a.remove();
    });
  } else if (ANDROID && glb) {
    btn.hidden = false;
    btn.addEventListener('click', () => {
      const params = new URLSearchParams({ file: abs(glb), mode: 'ar_preferred', resizable: 'false', title: stage.dataset.title || '' });
      location.href = `intent://arvr.google.com/scene-viewer/1.2?${params}#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(location.href)};end;`;
    });
  } else if ((usdz || glb) && stage.dataset.pageUrl && !/iphone|ipad|android/i.test(navigator.userAgent)) {
    const card = stage.querySelector('[data-ar-qr]'), code = stage.querySelector('[data-ar-qr-code]');
    if (!card || !code) return;
    btn.hidden = false;
    btn.addEventListener('click', async () => {
      if (!card.hidden) { card.hidden = true; return; }
      if (!code.firstChild) {
        const { default: qrcode } = await import('https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/+esm');
        const qr = qrcode(0, 'M'); qr.addData(stage.dataset.pageUrl); qr.make();
        code.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
      }
      card.hidden = false;
    });
    stage.querySelector('[data-ar-qr-close]').addEventListener('click', () => { card.hidden = true; });
  }
}

function init() {
  const stages = document.querySelectorAll('[data-b3d]:not([data-b3d-bound])');
  if (!stages.length) return;
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (!e.isIntersecting) return;
    io.unobserve(e.target);
    mountViewer(e.target).catch((err) => { e.target.classList.add('is-failed'); console.error('3D viewer', err); });
  }), { rootMargin: '300px 0px' });
  stages.forEach((s) => { s.dataset.b3dBound = ''; setupAR(s); io.observe(s); });
}
init();
document.addEventListener('shopify:section:load', init);
